import { TrackingService, TrackingFlow } from '../../../core/services/tracking.service';
import { CommonModule } from '@angular/common';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, OnInit, Output, afterNextRender, computed, inject, isDevMode, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { QuickActionOption, QuickActionResponse, QuickActionType, QuickActionValidation } from '../../../core/models/quick-action.model';
import { DOCUMENT_UPLOAD_ACCEPT, DocumentUploadApiResponse, DocumentUploadService, selectDocumentUploadFiles } from '../../../core/services/document-upload.service';
import { I18nService } from '../../../core/services/i18n.service';
import { QuickActionService } from '../../../core/services/quick-action.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import {
  CALLBACK_PERIODS,
  CALLBACK_WEEKDAYS,
  CallbackDayKey,
  CallbackPeriod,
  createCallbackAvailabilitySelection,
  formatCallbackAvailability,
  setCallbackAvailabilitySlot,
} from '../../utils/callback-availability';
import {
  documentUploadCommentCodePointLength,
  DOCUMENT_UPLOAD_COMMENT_MAX_CODE_POINTS,
  INPUT_VALIDATION_CODES,
  ibanValidationCode,
  isFutureOrTodayIsoDate,
  isPlausiblePhone,
  isValidContractDebitDay,
  isValidDebitDay,
  isValidEmail,
  isDocumentUploadCommentTooLong,
  isValidDocumentUploadComment,
  normalizeDocumentUploadComment,
  normalizeIban,
} from '../../utils/form-validators';
import { WidgetCardComponent, createTransferQr } from '../widget-card/widget-card.component';
import { navigatePaymentPopup, openPaymentPopup } from '../../utils/payment-popup';

interface PaymentClaimFallback {
  total: number | null;
  reference: string;
  creditor: string;
}

interface PaymentClaimSummary {
  total: string;
  reference: string;
  creditor: string;
}

interface TransferDetails {
  amount: string;
  recipient: string;
  iban: string;
  bic: string;
  bank: string;
  reference: string;
}

interface InstallmentScheduleRow {
  index: number;
  due_date: string;
  amount: string;
  amount_value?: number;
  is_final_installment?: boolean;
  has_deviating_final_installment_note?: boolean;
}

interface InstallmentScheduleSummary {
  regularCount: number;
  regularAmount: string;
  finalAmount: string;
  totalAmount: string;
}

type QuickActionValidationFields = Record<string, { code: string; message: string }>;

@Component({
  selector: 'app-quick-action-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, WidgetCardComponent, TranslatePipe],
  templateUrl: './quick-action-wizard.component.html',
  styleUrl: './quick-action-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickActionWizardComponent implements OnInit {
  private readonly quickActionService = inject(QuickActionService);
  private readonly documentUploadService = inject(DocumentUploadService);
  private readonly i18n = inject(I18nService);
  private readonly injector = inject(Injector);
  readonly dialogTitleId = 'quick-action-wizard-title';
  readonly showDevHelper = isDevMode();
  readonly documentUploadCommentMaxLength = DOCUMENT_UPLOAD_COMMENT_MAX_CODE_POINTS;
  readonly documentUploadAccept = DOCUMENT_UPLOAD_ACCEPT;

  @Input({ required: true }) actionType!: QuickActionType;
  @Input() paymentClaim: PaymentClaimFallback | null = null;
  @Input() initialStep: string | null = null;
  @Input() initialStepPayload: Record<string, unknown> | null = null;
  @Input() quickActionOrigin: 'quick_action' | 'chat' = 'quick_action';
  @Input() contactCallbackInitiallyExpanded = false;
  @Output() readonly close = new EventEmitter<void>();
  @Output() readonly completed = new EventEmitter<QuickActionResponse>();

  readonly response = signal<QuickActionResponse | null>(null);
  readonly stepHistory = signal<QuickActionResponse[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly contactAvailable = signal(false);
  readonly transferQrCode = signal<string | null>(null);
  readonly transferQrUnavailable = signal(false);
  readonly confirmCloseVisible = signal(false);
  readonly devTestDataConfirmation = signal<string | null>(null);
  readonly callbackWeekdays = CALLBACK_WEEKDAYS;
  readonly callbackPeriods = CALLBACK_PERIODS;

  readonly contact = signal({ email: '', telefon: '', message: '', callback_time_slot: '' });
  readonly contactCallbackExpanded = signal(false);
  readonly contactCallbackSelection = signal(createCallbackAvailabilitySelection());
  readonly documentUpload = signal({ filename: '', category: 'proof', reason: '', comment: '' });
  readonly documentFiles = signal<File[]>([]);
  readonly documentUploadProgress = signal(0);
  readonly sepa = signal<{ iban: string; bic?: string; abbuchungstag: string }>({
    iban: '', bic: '', abbuchungstag: '',
  });
  readonly sepaAccountHolder = computed(() => {
    const value = this.response()?.metadata?.['kontoinhaber'];
    return typeof value === 'string' ? value : '';
  });
  readonly sepaMandateAccepted = signal(false);
  readonly installmentContractAccepted = signal(false);
  private readonly viewedContractPayload = signal<string | null>(null);
  readonly contractViewed = computed(() => this.viewedContractPayload() === JSON.stringify(this.contractPdfPayload()));
  readonly contractViewRequired = signal(false);
  readonly submittedContractPayload = signal<Record<string, unknown> | null>(null);
    readonly installment = signal({
      start: '',
      duration_months: '',
      payment_method: 'transfer',
    });
  readonly paymentContract = signal({ abbuchungstag: '15' });
  private readonly emittedCompletionIds = new Set<string>();
  private paypalPopup: Window | null = null;

  private static readonly PAYMENT_OPTION_KEYS: Record<string, { label: string; hint: string }> = {
    transfer: {
      label: 'quickActionWizard.paymentOptions.transfer.label',
      hint: 'quickActionWizard.paymentOptions.transfer.hint',
    },
    sepa: {
      label: 'quickActionWizard.paymentOptions.sepa.label',
      hint: 'quickActionWizard.paymentOptions.sepa.hint',
    },
    installments: {
      label: 'quickActionWizard.paymentOptions.installments.label',
      hint: 'quickActionWizard.paymentOptions.installments.hint',
    },
    paypal: {
      label: 'quickActionWizard.paymentOptions.paypal.label',
      hint: 'quickActionWizard.paymentOptions.paypal.hint',
    },
  };

  private static readonly TITLE_KEYS_BY_STEP: Record<string, string> = {
    payment_method: 'quickActionWizard.titles.paymentMethod',
    transfer_details: 'quickActionWizard.titles.transferDetails',
    paypal_redirect: 'quickActionWizard.titles.paypalRedirect',
    sepa_mandate: 'quickActionWizard.titles.sepaMandate',
      installment_terms: 'quickActionWizard.titles.installmentTerms',
      installment_transfer_info: 'quickActionWizard.titles.installmentTransferInfo',
      installment_contact: 'quickActionWizard.titles.installmentContact',
      installment_contract_review: 'quickActionWizard.titles.installmentReview',
      payment_contract: 'quickActionWizard.titles.paymentContract',
    document_upload: 'quickActionWizard.titles.documentUpload',
    contact_form: 'quickActionWizard.titles.contactForm',
  };

  private readonly tracking = inject(TrackingService);
  private trackingFlow: TrackingFlow | null = null;

  ngOnDestroy(): void {
    if (this.trackingFlow) this.tracking.abortFlow(this.trackingFlow);
  }

  private trackPaymentFlow(installments: boolean): void {
    if (this.actionType !== 'payment') return;
    const flow = installments ? 'rzv' : 'payment';
    if (this.trackingFlow === flow) return;
    if (this.trackingFlow) this.tracking.abortFlow(this.trackingFlow);
    this.trackingFlow = flow;
    this.tracking.startFlow(flow);
  }

  ngOnInit(): void {
    this.startAction();
  }

  openContactForm(): void {
    if (this.trackingFlow) this.tracking.abortFlow(this.trackingFlow);
    this.trackingFlow = null;
    this.actionType = 'contact';
    this.initialStep = null;
    this.initialStepPayload = null;
    this.stepHistory.set([]);
    this.response.set(null);
    this.error.set(null);
    this.contactAvailable.set(false);
    this.startAction();
  }

  private startAction(): void {
    this.trackPaymentFlow(['installment_terms', 'installment_contract_review', 'payment_contract'].includes(this.initialStep ?? ''));
    this.contactCallbackExpanded.set(this.actionType === 'contact' && this.contactCallbackInitiallyExpanded);
    this.loading.set(true);
    const startPayload = this.initialStepPayload
      ? this.payloadWithOrigin(this.initialStepPayload)
      : undefined;
    this.quickActionService.start(this.actionType, startPayload).subscribe({
      next: (response) => {
        this.handleResponse(response);
        if (this.initialStep && response.status !== 'completed' && response.status !== 'blocked') {
          this.requestStep(this.initialStep, this.initialStepPayload ?? {});
        }
        this.loading.set(false);
      },
      error: (error) => {
        this.tracking.error('network', this.trackingFlow ?? undefined);
        this.error.set(this.backendErrorMessage(error, 'quickActionWizard.startError'));
        this.loading.set(false);
      },
    });
  }

  requestClose(): void {
    if (this.confirmCloseVisible()) {
      this.close.emit();
      return;
    }

    // Terminal states (success / blocked) hold no unsaved input -> close directly.
    if (this.canCloseWithoutConfirm()) {
      this.close.emit();
      return;
    }

    this.confirmCloseVisible.set(true);
  }

  private canCloseWithoutConfirm(): boolean {
    const status = this.response()?.status;
    return status === 'completed' || status === 'blocked';
  }

  cancelCloseRequest(): void {
    this.confirmCloseVisible.set(false);
  }

  confirmClose(): void {
    this.close.emit();
  }

  closeConfirmText(): string {
    const type = this.response()?.action_type ?? this.actionType;
    if (type === 'payment') {
      return this.i18n.t('quickActionWizard.confirmClosePayment');
    }
    return this.i18n.t('quickActionWizard.confirmCloseDefault');
  }

  canGoBack(): boolean {
    return !this.loading() && !this.contactAvailable() && this.stepHistory().length > 0;
  }

  goBack(): void {
    const history = this.stepHistory();
    const previous = history[history.length - 1];
    if (!previous) return;

    this.stepHistory.set(history.slice(0, -1));
    this.error.set(null);
    this.confirmCloseVisible.set(false);
    this.handleResponse(previous);
  }

  choosePaymentMethod(method: string): void {
    this.trackPaymentFlow(method === 'installments');
    const current = this.response();
    if (!current) return;

    if (method === 'paypal') {
      this.paypalPopup = openPaymentPopup();
      if (!this.paypalPopup) {
        this.error.set(this.i18n.t('quickActionWizard.paypalPopupBlocked'));
        return;
      }
    }

    this.requestStep('payment_method', { method });
  }

  submitInstallment(): void {
    if (!this.applyLocalValidation('installment_terms', this.validateInstallmentTermsLocally())) {
      return;
    }

    this.requestStep('installment_terms', this.installment());
  }

    completeTransfer(): void {
      const current = this.response();
      if (current?.current_step === 'installment_transfer_info') {
        this.requestStep('installment_transfer_info', {
          installment_contract: current.metadata?.['installment_contract'] ?? null,
        });
        return;
      }

      this.loading.set(true);
    this.error.set(null);
    if (this.trackingFlow) this.tracking.submitFlow(this.trackingFlow);
    this.quickActionService.complete('payment', 'transfer_details', {}).subscribe({
      next: (response) => {
        if (this.trackingFlow) {
          if (response.status === 'completed') this.tracking.completeFlow(this.trackingFlow);
          else this.tracking.error('validation', this.trackingFlow);
        }
        this.loading.set(false);
        this.close.emit();
      },
      error: (error) => {
        this.tracking.error('network', this.trackingFlow ?? undefined);
        this.error.set(this.backendErrorMessage(error, 'quickActionWizard.completeError'));
        this.loading.set(false);
      },
    });
  }

  submitSepa(): void {
    if (!this.applyLocalValidation('sepa_mandate', this.validateSepaLocally())) {
      return;
    }

    if (!this.sepaMandateAccepted()) {
      return;
    }

      const installmentContract = this.recordValue(this.response()?.metadata?.['installment_contract']);
      const payload = {
        ...this.sepa(),
        ...(installmentContract ? {
          abbuchungstag: this.firstText(installmentContract, ['rate_day']),
        } : {}),
        iban: normalizeIban(this.sepa().iban),
        installment_contract: installmentContract,
      };

      if (installmentContract) {
        this.requestStep('sepa_mandate', payload);
        return;
      }

      this.choosePaymentMethod('installments');
    }

    submitPaymentContract(): void {
    if (!this.applyLocalValidation('payment_contract', this.validatePaymentContractLocally())) {
      return;
    }

      this.complete('payment', 'payment_contract', this.paymentContract());
    }

    submitContact(): void {
      if (!this.applyLocalValidation('contact_form', this.validateContactLocally())) {
        return;
      }

      if (this.response()?.action_type === 'payment' && this.response()?.metadata?.['installment_contract']) {
        this.requestStep('installment_contact', {
          installment_contract: this.response()?.metadata?.['installment_contract'] ?? null,
          sepa_mandate: this.response()?.metadata?.['sepa_mandate'] ?? null,
          contact: this.contact(),
        });
        return;
      }

      this.complete('contact', 'contact_form', this.contact());
    }

    submitInstallmentReview(): void {
      const current = this.response();
      if (!current || !this.contractViewed() || !this.installmentContractAccepted() || this.loading()) return;

      const payload = structuredClone({
        installment_contract_confirmed: true,
        ...this.contractPdfPayload(),
        contact: current.metadata?.['contact'] ?? null,
      });
      this.submittedContractPayload.set(payload);
      this.complete('payment', 'installment_contract_review', payload);
    }

    confirmInstallmentContract(event: Event): void {
      const checkbox = event.target as HTMLInputElement;
      this.contractViewRequired.set(checkbox.checked && !this.contractViewed());
      checkbox.checked = checkbox.checked && this.contractViewed();
      this.installmentContractAccepted.set(checkbox.checked);
    }

    private contractPdfPayload(): Record<string, unknown> {
      const current = this.response();
      return {
        installment_contract: current?.metadata?.['installment_contract'] ?? null,
        sepa_mandate: current?.metadata?.['sepa_mandate'] ?? null,
        abbuchungstag: current ? this.installmentContractRateDay(current) : null,
      };
    }

    downloadInstallmentContract(event: Event): void {
      event.preventDefault();
      event.stopPropagation();
      const current = this.response();
      if (!current || this.loading()) return;
      const payload = current.status === 'completed' ? this.submittedContractPayload() : this.contractPdfPayload();
      if (!payload) return;
      const viewedPayload = JSON.stringify(payload);

      const popup = openPaymentPopup();
      if (!popup) {
        this.error.set(this.i18n.t('quickActionWizard.stepError'));
        return;
      }

      this.loading.set(true);
      this.error.set(null);
      this.quickActionService.downloadInstallmentContract(this.payloadWithOrigin(payload)).subscribe({
        next: (response) => {
          const blob = response.body;
          if (!blob || blob.size === 0 || blob.type !== 'application/pdf') {
            popup.close();
            this.error.set(this.i18n.t('quickActionWizard.stepError'));
            this.loading.set(false);
            return;
          }
          const url = URL.createObjectURL(blob);
          popup.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
          navigatePaymentPopup(popup, url);
          this.viewedContractPayload.set(viewedPayload);
          this.contractViewRequired.set(false);
          this.loading.set(false);
        },
        error: (error) => {
        this.tracking.error('network', this.trackingFlow ?? undefined);
          popup.close();
          this.error.set(this.backendErrorMessage(error, 'quickActionWizard.stepError'));
          this.loading.set(false);
        },
      });
    }

  submitDocumentUpload(): void {
    const files = this.documentFiles();
    const localFields: QuickActionValidationFields = {};
    if (files.length === 0) {
      localFields['filename'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidModalPayload, this.i18n.t('quickActionWizard.fileRequired'));
    }
    if (this.documentUpload().reason.trim() === '') {
      localFields['reason'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidModalPayload, this.i18n.t('documentUploadModal.reasonRequired'));
    }
    if (!this.applyLocalValidation('document_upload', localFields)) {
      return;
    }
    const documentUpload = {
      ...this.documentUpload(),
      comment: normalizeDocumentUploadComment(this.documentUpload().comment),
    };
    // The comment error is rendered live at the field.
    if (!isValidDocumentUploadComment(documentUpload.comment)) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.documentUploadProgress.set(0);
    this.documentUploadService.upload(files, {
      ...documentUpload,
      uploadContext: 'quick_action_document_upload',
      quickActionUploadToken: this.quickActionUploadToken(),
    }).subscribe({
      next: (event) => this.handleDocumentUploadEvent(event),
      error: (error) => {
        this.tracking.error('network', this.trackingFlow ?? undefined);
        this.error.set(this.backendErrorMessage(error, 'quickActionWizard.uploadError'));
        this.loading.set(false);
      },
    });
  }

  onDocumentFileSelect(event: Event): void {
    const files = Array.from((event.target as HTMLInputElement).files ?? []);
    if (files.length === 0) return;
    const selection = selectDocumentUploadFiles([], files, (key) => this.i18n.t(key));
    this.documentFiles.set(selection.files);
    this.documentUpload.update((value) => ({ ...value, filename: selection.files.map((file) => file.name).join(', ') }));
    this.error.set(selection.error);
  }

  updateContact(field: 'email' | 'telefon' | 'message' | 'callback_time_slot', value: string): void {
    this.contact.update((current) => ({ ...current, [field]: value }));
  }

  toggleContactCallbackExpanded(trigger: HTMLElement): void {
    this.contactCallbackExpanded.update((expanded) => !expanded);
    if (this.contactCallbackExpanded()) {
      // Bring every new slot into view at once; the body's scroll-padding keeps it clear of the sticky actions bar.
      afterNextRender(() => document.getElementById(trigger.getAttribute('aria-controls')!)?.scrollIntoView({ block: 'center' }), { injector: this.injector });
    }
  }

  isContactCallbackSlotSelected(dayKey: CallbackDayKey, period: CallbackPeriod): boolean {
    return this.contactCallbackSelection()[dayKey][period];
  }

  onContactCallbackSlotChange(dayKey: CallbackDayKey, period: CallbackPeriod, event: Event): void {
    const selected = (event.target as HTMLInputElement).checked;
    const nextSelection = setCallbackAvailabilitySlot(this.contactCallbackSelection(), dayKey, period, selected);
    this.contactCallbackSelection.set(nextSelection);
    this.updateContact('callback_time_slot', formatCallbackAvailability(nextSelection));
  }

  callbackDayLabel(dayKey: CallbackDayKey): string {
    return this.translatedOrNull(`callbackAvailability.days.${dayKey}`) ?? dayKey;
  }

  callbackPeriodLabel(period: CallbackPeriod): string {
    return this.translatedOrNull(`callbackAvailability.periods.${period}`) ?? period;
  }

  contactCallbackTimeSlotLabel(): string {
    return this.callbackWeekdays
      .map((day) => {
        const periods = this.callbackPeriods
          .filter((period) => this.contactCallbackSelection()[day.key][period])
          .map((period) => this.callbackPeriodLabel(period));
        return periods.length > 0 ? `${this.callbackDayLabel(day.key)} (${periods.join(', ')})` : '';
      })
      .filter(Boolean)
      .join(', ');
  }

  updateDocumentUpload(field: 'category' | 'reason' | 'comment', value: string): void {
    this.documentUpload.update((current) => ({ ...current, [field]: value }));
  }

  hasValidDocumentUploadComment(): boolean {
    return isValidDocumentUploadComment(this.documentUpload().comment);
  }

  documentUploadCommentLength(): number {
    return documentUploadCommentCodePointLength(this.documentUpload().comment);
  }

  isDocumentUploadCommentTooLong(): boolean {
    return isDocumentUploadCommentTooLong(this.documentUpload().comment);
  }

  updateSepa(field: 'iban' | 'bic' | 'abbuchungstag', value: string): void {
    this.sepa.update((current) => ({
      ...current,
      [field]: field === 'iban' ? normalizeIban(value) : (field === 'bic' ? value.trim().toUpperCase() : value),
    }));
  }

  updateSepaMandateAccepted(value: boolean): void {
    this.sepaMandateAccepted.set(value);
  }

    updateInstallment(field: 'start' | 'duration_months' | 'payment_method', value: string): void {
      const previousValue = this.installment()[field];
      this.installment.update((current) => ({ ...current, [field]: value }));
      if ((field === 'start' || field === 'duration_months') && previousValue !== value) {
        this.sepa.update((current) => ({ ...current, abbuchungstag: '' }));
        this.sepaMandateAccepted.set(false);
      }
    }

  updatePaymentContract(field: 'abbuchungstag', value: string): void {
    this.paymentContract.update((current) => ({ ...current, [field]: value }));
  }

  insertSepaTestData(): void {
    if (!this.showDevHelper) return;
    this.sepa.set({
      iban: 'DE89370400440532013000',
      bic: 'MARKDEF1100',
      abbuchungstag: '15',
    });
    this.sepaMandateAccepted.set(true);
    this.markDevTestDataInserted(this.i18n.t('quickActionWizard.devSepaInserted'));
  }

  insertInstallmentTestData(): void {
    if (!this.showDevHelper) return;
      this.installment.update((current) => ({
        ...current,
        start: this.defaultInstallmentStartDate(),
        duration_months: current.duration_months || '6',
      }));
    this.markDevTestDataInserted(this.i18n.t('quickActionWizard.devInstallmentInserted'));
  }

  insertPaymentContractTestData(): void {
    if (!this.showDevHelper) return;
    this.paymentContract.set({
      abbuchungstag: '15',
    });
    this.markDevTestDataInserted(this.i18n.t('quickActionWizard.devContractInserted'));
  }

  insertDocumentUploadTestData(): void {
    if (!this.showDevHelper) return;
    const file = new File(["%PDF-1.4\n% Test upload\n%%EOF\n"], 'test-upload.pdf', { type: 'application/pdf' });
    this.documentFiles.set([file]);
    this.documentUpload.set({
      filename: 'test-upload.pdf',
      category: 'proof',
      reason: 'Zahlungsnachweis',
      comment: 'TESTDATEN Upload.',
    });
    this.markDevTestDataInserted(this.i18n.t('quickActionWizard.devUploadInserted'));
  }

  insertContactTestData(): void {
    if (!this.showDevHelper) return;
    if (this.isInstallmentContactContext(this.response())) {
      this.contact.set({
        email: 'testdaten.kontakt@example.com',
        telefon: '+49 151 23456789',
        message: '',
        callback_time_slot: '',
      });
      this.markDevTestDataInserted(this.i18n.t('quickActionWizard.devContactInserted'));
      return;
    }

    const callbackSelection = setCallbackAvailabilitySlot(
      createCallbackAvailabilitySelection(),
      'Mo',
      'Vormittag',
      true
    );
    this.contactCallbackSelection.set(callbackSelection);
    this.contact.set({
      email: 'testdaten.kontakt@example.com',
      telefon: '+49 151 23456789',
      message: 'TESTDATEN Kontaktanfrage zur sicheren Endurance-Pruefung.',
      callback_time_slot: formatCallbackAvailability(callbackSelection),
    });
    this.markDevTestDataInserted(this.i18n.t('quickActionWizard.devContactInserted'));
  }

  validationMessage(field: string): string {
    const validation = this.validation();
    const fieldState = validation?.fields[field];
    if (!fieldState) {
      return '';
    }

    const step = this.response()?.current_step ?? 'default';
    const localized = this.translatedOrNull(`quickActionWizard.validation.${step}.fields.${field}.${fieldState.code}`)
      ?? this.translatedOrNull(`quickActionWizard.validation.default.fields.${field}`);
    if (localized !== null) {
      return localized;
    }

    if (this.i18n.language() !== 'de') {
      return this.translatedOrNull('quickActionWizard.validation.default.fieldFallback')
        ?? this.i18n.t('quickActionWizard.validation.default.summary');
    }

    return fieldState.message;
  }

  hasFieldError(field: string): boolean {
    return this.validationMessage(field) !== '';
  }

  validationSummary(): string {
    const validation = this.validation();
    if (!validation) {
      return '';
    }

    const step = this.response()?.current_step ?? 'default';
    const localized = this.translatedOrNull(`quickActionWizard.validation.${step}.summary`);
    if (localized !== null) {
      return localized;
    }

    if (this.i18n.language() !== 'de') {
      return this.translatedOrNull('quickActionWizard.validation.default.summary')
        ?? this.i18n.t('quickActionWizard.completeError');
    }

    return validation.message;
  }

  paymentOptionStatus(option: QuickActionOption): string {
    if (!option.enabled) return this.i18n.t('quickActionWizard.unavailable');
    return this.i18n.t('quickActionWizard.select');
  }

  localizedTitle(current: QuickActionResponse): string {
    const contextKey = this.contextualTitleKey(current);
    if (contextKey) {
      return this.i18n.t(contextKey);
    }

    const key = QuickActionWizardComponent.TITLE_KEYS_BY_STEP[current.current_step];
    return key ? this.i18n.t(key) : current.title || this.i18n.t('quickActionWizard.fallbackTitle');
  }

  localizedCompletionMessage(current: QuickActionResponse): string {
    const contextKey = this.contextualCompletionKey(current);
    if (contextKey) {
      return this.i18n.t(contextKey);
    }

    return this.translatedOrNull(`quickActionWizard.completion.${current.action_type}.${current.current_step}.${current.status}`)
      ?? current.completion_message ?? '';
  }

  private contextualTitleKey(current: QuickActionResponse): string | null {
    if (current.current_step === 'sepa_mandate' && /ratenzahlung/i.test(current.title ?? '')) {
      return 'quickActionWizard.titles.installmentSepaMandate';
    }

    return null;
  }

  private contextualCompletionKey(current: QuickActionResponse): string | null {
    if (
      current.action_type === 'payment'
      && current.current_step === 'sepa_mandate'
      && current.status === 'completed'
      && /ratenzahlung/i.test(current.completion_message ?? '')
    ) {
      return 'quickActionWizard.completion.payment.sepa_mandate.installmentCompleted';
    }

    return null;
  }

  isInstallmentContext(current: QuickActionResponse | null | undefined): boolean {
    return Boolean(current?.metadata?.['installment_contract']);
  }

  isInstallmentContactContext(current: QuickActionResponse | null | undefined): boolean {
    return current?.action_type === 'payment'
      && current.current_step === 'contact_form'
      && Boolean(current.metadata?.['installment_contract']);
  }

  localizedPaymentOptionLabel(option: QuickActionOption): string {
    const key = QuickActionWizardComponent.PAYMENT_OPTION_KEYS[option.id]?.label;
    return key ? this.i18n.t(key) : option.label;
  }

  localizedPaymentOptionHint(option: QuickActionOption): string {
    const key = QuickActionWizardComponent.PAYMENT_OPTION_KEYS[option.id]?.hint;
    return key ? this.i18n.t(key) : option.hint ?? '';
  }

  documentFilesStatus(): string {
    const count = this.documentFiles().length;
    if (count === 0) {
      return this.i18n.t('documentUploadModal.noFilesSelected');
    }

    return this.i18n.t('documentUploadModal.selectedFiles', { count });
  }

  validation(): QuickActionValidation | null {
    const validation = this.response()?.validation_errors;
    if (!validation || Array.isArray(validation)) {
      return null;
    }

    return validation;
  }

  isPaymentStart(current: QuickActionResponse): boolean {
    return current.action_type === 'payment' && current.current_step === 'payment_method';
  }

  shouldShowWidgetCards(current: QuickActionResponse): boolean {
    return current.widgets.length > 0
      && !this.isPaymentStart(current)
        && current.current_step !== 'transfer_details'
        && current.current_step !== 'installment_transfer_info'
        && current.current_step !== 'installment_terms'
        && current.current_step !== 'payment_contract'
        && current.current_step !== 'installment_contract_review';
  }

  paymentClaimSummary(current: QuickActionResponse): PaymentClaimSummary | null {
    const claimWidget = current.widgets.find((widget) => widget.type === 'claim_overview');
    const claimData = claimWidget?.data ?? {};
    const responseData = {
      ...(current.metadata ?? {}),
      ...(current.form_schema?.conditions ?? {}),
    };
    const total = this.firstDisplayValue(
      claimData,
      ['open_total_amount', 'gesamtforderung', 'gesamt_forderung', 'totalAmount', 'total_amount', 'claim_total'],
      responseData,
      ['open_total_amount', 'gesamtforderung', 'gesamt_forderung', 'totalAmount', 'total_amount', 'claim_total'],
      this.paymentClaim?.total ?? null
    );

    if (!total) {
      return null;
    }

    return {
      total,
      reference: this.firstText(claimData, ['aktenzeichen', 'aktennummer', 'case_number']) || this.paymentClaim?.reference || '',
      creditor: this.firstText(claimData, ['glaeubiger', 'creditorName', 'creditor_name']) || this.paymentClaim?.creditor || '',
    };
  }

    transferDetails(current: QuickActionResponse): TransferDetails | null {
    const bankData = this.bankDetailsData(current);
    if (!bankData) {
      return null;
    }

    const epc = this.recordValue(bankData['epc']);

    return {
      amount: this.firstText(epc ?? {}, ['amountText'])
        || this.firstText(bankData, ['amountText', 'betrag'])
        || this.paymentClaimSummary(current)?.total
        || '',
      recipient: this.cleanTransferValue(this.firstText(bankData, ['empfaenger', 'recipient', 'name'])),
      iban: this.cleanTransferValue(this.firstText(bankData, ['iban', 'bank_iban'])),
      bic: this.cleanTransferValue(this.firstText(bankData, ['bic', 'bank_bic'])),
      bank: this.cleanTransferValue(this.firstText(bankData, ['bank', 'bank_name'])),
        reference: this.cleanTransferValue(this.firstText(bankData, ['verwendungszweck', 'reference', 'purpose'])),
      };
    }

      installmentContract(current: QuickActionResponse): Record<string, unknown> {
        return this.recordValue(current.metadata?.['installment_contract'])
          ?? this.recordValue(current.modal?.modalData)
          ?? {};
      }

      installmentContractValue(current: QuickActionResponse, key: string): string {
        const value = this.firstText(this.installmentContract(current), [key]);
        return key === 'start' ? this.i18n.formatDate(value) : value;
      }

      feeWaivedUntilRates(current: QuickActionResponse): number {
        const value = current.form_schema?.conditions?.['feeWaivedUntilRates'];
        return typeof value === 'number' ? value : 2;
      }

      installmentSchedule(current: QuickActionResponse): InstallmentScheduleRow[] {
      const contract = this.installmentContract(current);
      return this.installmentScheduleRows(contract['schedule'] ?? contract['ratenplan']);
    }

    selectedInstallmentSummary(current: QuickActionResponse): InstallmentScheduleSummary | null {
      const plan = this.selectedInstallmentPlan(current);
      return this.summarizeInstallments(this.installmentScheduleRows(plan?.['schedule']), this.firstText(plan ?? {}, ['total']));
    }

    installmentSummary(current: QuickActionResponse): InstallmentScheduleSummary | null {
      const contract = this.installmentContract(current);
      return this.summarizeInstallments(this.installmentSchedule(current), this.firstText(contract, ['total']));
    }

    private installmentScheduleRows(schedule: unknown): InstallmentScheduleRow[] {
      if (!Array.isArray(schedule)) {
        return [];
      }

      return schedule
        .filter((row): row is Record<string, unknown> => row !== null && typeof row === 'object' && !Array.isArray(row))
        .map((row, index) => ({
          index: typeof row['index'] === 'number' ? row['index'] : index + 1,
          due_date: this.i18n.formatDate(this.firstText(row, ['due_date'])),
          amount: this.firstText(row, ['amount']),
          amount_value: typeof row['amount_value'] === 'number' ? row['amount_value'] : undefined,
          is_final_installment: row['is_final_installment'] === true,
          has_deviating_final_installment_note: row['has_deviating_final_installment_note'] === true,
        }));
    }

    private summarizeInstallments(schedule: InstallmentScheduleRow[], totalAmount: string): InstallmentScheduleSummary | null {
      const regular = schedule.slice(0, -1);
      const finalAmount = schedule.at(-1)?.amount;
      if (regular.length === 0 || !finalAmount || !regular[0].amount
        || regular.some((row) => row.amount !== regular[0].amount)) {
        return null;
      }

      return { regularCount: regular.length, regularAmount: regular[0].amount, finalAmount, totalAmount };
    }

    installmentContactData(current: QuickActionResponse): Record<string, unknown> | null {
      const contact = this.recordValue(current.metadata?.['contact']);
      const hasValue = contact !== null && Object.values(contact)
        .some((value) => typeof value === 'string' && value.trim() !== '');
      return hasValue ? contact : null;
    }

    firstInstallmentDue(current: QuickActionResponse): string {
      return this.installmentSchedule(current)[0]?.due_date
        || this.installmentContractValue(current, 'start')
        || '-';
    }

    installmentPaymentMethodLabel(current: QuickActionResponse): string {
      return this.installmentPaymentMethod(current) === 'sepa'
        ? this.i18n.t('quickActionWizard.directDebit')
        : this.i18n.t('quickActionWizard.transfer');
    }

    sepaMandateData(current: QuickActionResponse): Record<string, unknown> | null {
      return this.recordValue(current.metadata?.['sepa_mandate']);
    }

    installmentPaymentMethod(current: QuickActionResponse): string {
      const method = this.installmentContract(current)['payment_method'];
      return typeof method === 'string' ? method : this.installment().payment_method;
    }

    selectedInstallmentPlan(current: QuickActionResponse): Record<string, unknown> | null {
      const plans = this.recordValue(current.form_schema?.conditions?.['plansByDuration']);
      const selectedDuration = this.installment().duration_months || this.installmentDurationDefault(current);
      return this.recordValue(plans?.[selectedDuration]);
    }

    selectedInstallmentRate(current: QuickActionResponse): string {
      const selectedPlan = this.selectedInstallmentPlan(current);
      return this.firstText(selectedPlan ?? {}, ['rate'])
        || this.firstText(current.form_schema?.conditions ?? {}, ['rate6MonthsWithFee', 'defaultRate', 'rate6Months']);
    }

    selectedInstallmentFeeTotal(current: QuickActionResponse): string {
      return this.selectedInstallmentFee(current, 'rzvFeeTotal');
    }

    selectedInstallmentFeeHonorar(current: QuickActionResponse): string {
      return this.selectedInstallmentFee(current, 'rzvFeeHonorar');
    }

    selectedInstallmentFeeAuslagen(current: QuickActionResponse): string {
      return this.selectedInstallmentFee(current, 'rzvFeeAuslagen');
    }

    selectedInstallmentFeeIsWaived(current: QuickActionResponse): boolean {
      const selectedDuration = Number.parseInt(this.installment().duration_months || this.installmentDurationDefault(current), 10);
      return Number.isInteger(selectedDuration) && selectedDuration <= this.feeWaivedUntilRates(current);
    }

    installmentFeeForPaidPlan(current: QuickActionResponse): string {
      return this.firstText(current.form_schema?.conditions ?? {}, ['rzvFeeTotal']);
    }

    private selectedInstallmentFee(current: QuickActionResponse, key: string): string {
      const selectedDuration = Number.parseInt(this.installment().duration_months || this.installmentDurationDefault(current), 10);
      if (this.selectedInstallmentFeeIsWaived(current)) {
        return '0,00 €';
      }
      return this.firstText(current.form_schema?.conditions ?? {}, [key]) || '0,00 €';
    }

    installmentStartOptions(current: QuickActionResponse): Array<{ id: string; label: string }> {
      const field = current.form_schema?.fields.find((candidate) => candidate.name === 'start');
      const options = Array.isArray(field?.options) ? field.options : [];
      return options
        .map((option) => this.recordValue(option))
        .filter((option): option is Record<string, unknown> => (
          option !== null
          && typeof option['id'] === 'string'
          && typeof option['label'] === 'string'
        ))
        .map((option) => ({
          id: option['id'] as string,
          label: this.i18n.formatDate(option['id'] as string),
        }));
    }

    installmentDurationOptions(current: QuickActionResponse): Array<{ id: string; label: string }> {
      const field = current.form_schema?.fields.find((candidate) => candidate.name === 'duration_months');
      return (Array.isArray(field?.options) ? field.options : [])
        .map((option) => this.recordValue(option))
        .filter((option): option is Record<string, unknown> => option !== null && typeof option['id'] === 'string')
        .map((option) => ({ id: option['id'] as string, label: this.firstText(option, ['label']) }));
    }

    installmentDurationLabel(duration: string): string {
      return this.translatedOrNull(`quickActionWizard.installmentCountOptions.${duration}`)
        ?? this.i18n.t('quickActionWizard.installmentCountOption', { count: duration });
    }

    installmentContractRateDay(current: QuickActionResponse): string {
      const rateDay = this.installmentContract(current)['rate_day'];
      return typeof rateDay === 'string' && rateDay.trim() !== '' ? rateDay : this.paymentContract().abbuchungstag;
    }

    installmentDebitDayLabel(current: QuickActionResponse): string {
      return this.installmentPaymentMethod(current) === 'sepa'
        ? this.i18n.t('modalCommon.debitDay')
        : this.i18n.t('quickActionWizard.rateDate');
    }

    installmentDebitDayValue(current: QuickActionResponse): string {
      const day = this.installmentContractRateDay(current);
      return day ? this.i18n.t('quickActionWizard.monthDay', { day }) : '';
    }

  private requestStep(step: string, payload: Record<string, unknown>): void {
    const previous = this.response();
    this.loading.set(true);
    this.error.set(null);
    this.devTestDataConfirmation.set(null);
    this.quickActionService.step('payment', step, this.payloadWithOrigin(payload)).subscribe({
      next: (response) => {
        if (previous && response.status !== 'validation_error' && response.current_step !== previous.current_step) {
          this.stepHistory.update((history) => [...history, previous]);
        }
        this.handleResponse(response);
        this.loading.set(false);
        // CN-02: the transfer selection carries a one-time chat reminder while the wizard stays open.
        if (response.status === 'in_progress' && response.current_step === 'transfer_details' && response.completion_message) {
          this.completed.emit(response);
        }
        if (this.shouldStartPaypalFullPayment(response)) {
          this.startPaypalFullPayment();
        } else {
          this.closePaypalPopup();
        }
      },
      error: (error) => {
        this.tracking.error('network', this.trackingFlow ?? undefined);
        this.closePaypalPopup();
        this.error.set(this.backendErrorMessage(error, 'quickActionWizard.stepError'));
        this.loading.set(false);
      },
    });
  }

  private firstDisplayValue(
    primaryData: Record<string, any>,
    primaryKeys: string[],
    secondaryData: Record<string, unknown>,
    secondaryKeys: string[],
    fallbackAmount: number | null
  ): string {
    return this.firstText(primaryData, primaryKeys)
      || this.firstText(secondaryData, secondaryKeys)
      || this.formatCurrency(fallbackAmount);
  }

  private firstText(data: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = data[key];
      if (typeof value === 'string' && value.trim() !== '') {
        return value.trim();
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return this.formatCurrency(value);
      }
    }

    return '';
  }

  private firstPlainText(data: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = data[key];
      if (typeof value === 'string' && value.trim() !== '') {
        return value.trim();
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }

    return '';
  }

  private formatCurrency(amount: number | null): string {
    if (amount === null || !Number.isFinite(amount)) {
      return '';
    }

    return this.i18n.formatCurrency(amount);
  }

  private complete(actionType: QuickActionType, step: string, payload: Record<string, unknown>): void {
    if (this.trackingFlow) this.tracking.submitFlow(this.trackingFlow);
    this.loading.set(true);
    this.error.set(null);
    this.devTestDataConfirmation.set(null);
    this.quickActionService.complete(actionType, step, this.payloadWithOrigin(payload)).subscribe({
      next: (response) => {
        this.handleResponse(response);
        this.loading.set(false);
      },
      error: (error) => {
        this.tracking.error('network', this.trackingFlow ?? undefined);
        this.error.set(this.backendErrorMessage(error, 'quickActionWizard.completeError'));
        this.loading.set(false);
      },
    });
  }

  private payloadWithOrigin(payload: Record<string, unknown>): Record<string, unknown> {
    if (this.quickActionOrigin !== 'chat') {
      return payload;
    }

    return {
      ...payload,
      __quick_action_origin: 'chat',
      ...(typeof this.initialStepPayload?.['modal_nonce'] === 'string'
        ? { modal_nonce: this.initialStepPayload['modal_nonce'] }
        : {}),
    };
  }

  private shouldStartPaypalFullPayment(response: QuickActionResponse): boolean {
    return response.action_type === 'payment'
      && response.current_step === 'paypal_redirect'
      && response.status === 'in_progress';
  }

  private startPaypalFullPayment(): void {
    this.loading.set(true);
    this.error.set(null);
    this.devTestDataConfirmation.set(null);
    this.quickActionService.startPaypalFullPayment().subscribe({
      next: (response) => {
        this.loading.set(false);
        if (this.paypalPopup) {
          this.tracking.paypalRedirect(this.paypalPopup);
          navigatePaymentPopup(this.paypalPopup, response.data.approval_url);
          this.paypalPopup = null;
        }
      },
      error: (error) => {
        this.tracking.error('network', this.trackingFlow ?? undefined);
        this.closePaypalPopup();
        this.loading.set(false);
        this.error.set(this.backendErrorMessage(error, 'quickActionWizard.paypalStartError'));
      },
    });
  }

  private closePaypalPopup(): void {
    this.paypalPopup?.close();
    this.paypalPopup = null;
  }

  private backendErrorMessage(error: unknown, fallbackKey: string): string {
    const body = this.errorEnvelope(error);
    const code = this.errorCode(body);
    this.contactAvailable.set(['VALIDATION_FAILED', 'INSTALLMENT_CONTRACT_PDF_UNAVAILABLE', 'PAYPAL_NOT_ALLOWED', 'PAYPAL_ORDER_NOT_FOUND'].includes(code ?? ''));
    if (['VALIDATION_FAILED', 'INSTALLMENT_CONTRACT_PDF_UNAVAILABLE', 'PAYPAL_NOT_ALLOWED', 'PAYPAL_ORDER_NOT_FOUND', 'PAYPAL_DISABLED'].includes(code ?? '')
      && typeof body?.message === 'string') {
      return body.message;
    }
    const localizedByCode = code ? this.translatedOrNull(`quickActionWizard.errors.${code}`) : null;
    if (localizedByCode) {
      return localizedByCode;
    }

    if (body && this.i18n.language() === 'de') {
      const message = body.message ?? body.error;
      if (typeof message === 'string' && message.trim() !== '') {
        return message.trim();
      }
    }

    return this.i18n.t(fallbackKey);
  }

  private errorEnvelope(error: unknown): { code?: unknown; message?: unknown; error?: unknown } | null {
    if (!error || typeof error !== 'object' || !('error' in error)) {
      return null;
    }

    const body = (error as { error?: unknown }).error;
    return body && typeof body === 'object'
      ? body as { code?: unknown; message?: unknown; error?: unknown }
      : null;
  }

  private errorCode(body: { code?: unknown; error?: unknown } | null): string | null {
    if (!body) {
      return null;
    }

    if (typeof body.code === 'string' && body.code.trim() !== '') {
      return body.code.trim();
    }

    if (body.error && typeof body.error === 'object') {
      const nestedCode = (body.error as { code?: unknown }).code;
      return typeof nestedCode === 'string' && nestedCode.trim() !== '' ? nestedCode.trim() : null;
    }

    return null;
  }

  private translatedOrNull(key: string): string | null {
    return this.i18n.has(key) ? this.i18n.t(key) : null;
  }

  private handleDocumentUploadEvent(event: HttpEvent<DocumentUploadApiResponse>): void {
    if (event.type === HttpEventType.UploadProgress) {
      const total = event.total ?? this.documentFiles().reduce((sum, file) => sum + file.size, 0);
      if (total > 0) {
        this.documentUploadProgress.set(Math.min(100, Math.round((event.loaded / total) * 100)));
      }
      return;
    }

    if (event.type !== HttpEventType.Response) {
      return;
    }

    const uploads = event.body?.data.uploads ?? [];
    const filenames = uploads.map((upload) => upload.original_name);
    this.documentUploadProgress.set(100);
    this.complete('document_upload', 'document_upload', {
      ...this.documentUpload(),
      comment: normalizeDocumentUploadComment(this.documentUpload().comment),
      filename: filenames[0] ?? this.documentUpload().filename,
      file_count: uploads.length,
      quick_action_upload_token: this.quickActionUploadToken(),
    });
  }

  private quickActionUploadToken(): string {
    const token = this.response()?.metadata?.['quick_action_upload_token'];
    return typeof token === 'string' ? token : '';
  }

    private handleResponse(response: QuickActionResponse): void {
      this.contactAvailable.set(response.status === 'blocked');
      if (['installment_terms', 'installment_transfer_info', 'installment_contract_review', 'payment_contract'].includes(response.current_step) || response.metadata?.['installment_contract']) this.trackPaymentFlow(true);
      if (response.status === 'validation_error' || response.status === 'blocked') this.tracking.error('validation', this.trackingFlow ?? undefined);
      else if (response.status === 'completed' && this.trackingFlow) this.tracking.completeFlow(this.trackingFlow);
      this.confirmCloseVisible.set(false);
      this.devTestDataConfirmation.set(null);
      this.applyResponseDefaults(response);
      this.response.set(response);
      this.emitCompletionOnce(response);
      void this.updateTransferQr(response);
    }

    private emitCompletionOnce(response: QuickActionResponse): void {
      if (response.status !== 'completed') {
        return;
      }
      if (this.emittedCompletionIds.has(response.action_id)) {
        return;
      }

      this.emittedCompletionIds.add(response.action_id);
      this.completed.emit(response);
    }

    private applyResponseDefaults(response: QuickActionResponse): void {
      if (response.current_step === 'installment_terms') {
        const prefill = this.recordValue(response.metadata?.['prefill']) ?? {};
        const start = this.firstText(prefill, ['start']) || this.schemaDefault(response, 'start');
        const duration = this.installmentDurationDefault(response, prefill);
        const paymentMethod = this.firstText(prefill, ['payment_method']) || this.schemaDefault(response, 'payment_method');
        this.installment.update((current) => ({
          ...current,
          start: current.start || start,
          duration_months: response.status === 'validation_error'
            ? (current.duration_months || duration)
            : (duration || current.duration_months),
          payment_method: ['transfer', 'sepa'].includes(paymentMethod) ? paymentMethod : current.payment_method,
        }));
      }

      if (response.current_step === 'sepa_mandate') {
        const defaultDay = this.schemaDefault(response, 'abbuchungstag');
        if (!this.isInstallmentContext(response) && defaultDay) {
          this.sepa.update((current) => ({ ...current, abbuchungstag: current.abbuchungstag || defaultDay }));
        }
      }

      if (response.current_step === 'installment_contract_review') {
        this.installmentContractAccepted.set(false);
        const rateDay = this.firstText(this.installmentContract(response), ['rate_day']);
        if (rateDay) {
          this.paymentContract.set({ abbuchungstag: rateDay });
        }
      }
    }

    private schemaDefault(response: QuickActionResponse, fieldName: string): string {
      const field = response.form_schema?.fields.find((candidate) => candidate.name === fieldName);
      const value = field?.defaultValue;
      if (typeof value === 'string') return value;
      if (typeof value === 'number' && Number.isFinite(value)) return String(value);
      return '';
    }

    private installmentDurationDefault(response: QuickActionResponse, prefill: Record<string, unknown> | null = null): string {
      const prefillData = prefill ?? this.recordValue(response.metadata?.['prefill']) ?? {};
      return this.firstPlainText(prefillData, ['duration_months'])
        || this.firstPlainText(response.metadata ?? {}, ['duration_months'])
        || this.firstPlainText(response.form_schema?.conditions ?? {}, ['duration_months', 'defaultDurationMonths'])
        || this.schemaDefault(response, 'duration_months');
    }

  private markDevTestDataInserted(message: string): void {
    this.error.set(null);
    this.confirmCloseVisible.set(false);
    this.devTestDataConfirmation.set(message);
    // The status renders at the top of the body while the helper button sits at the bottom.
    afterNextRender(() => document.querySelector('.quick-action-wizard__dev-confirmation')?.scrollIntoView({ block: 'nearest' }), { injector: this.injector });
    this.tracking.error('validation', this.trackingFlow ?? undefined);
    this.response.update((current) => current
      ? {
        ...current,
        status: current.status === 'validation_error' ? 'in_progress' : current.status,
        validation_errors: [],
      }
      : current
    );
  }

  private defaultInstallmentStartDate(): string {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(15);
    return nextMonth.toISOString().slice(0, 10);
  }

  private async updateTransferQr(response: QuickActionResponse): Promise<void> {
    this.transferQrCode.set(null);
    this.transferQrUnavailable.set(false);

    if (response.current_step !== 'transfer_details') {
      return;
    }

    try {
      const qrCode = await createTransferQr(this.bankDetailsData(response), amount => this.formatCurrency(amount));
      if (this.response()?.action_id === response.action_id) {
        this.transferQrCode.set(qrCode);
        this.transferQrUnavailable.set(qrCode === null);
      }
    } catch {
      if (this.response()?.action_id === response.action_id) {
        this.transferQrUnavailable.set(true);
      }
    }
  }

  private bankDetailsData(response: QuickActionResponse): Record<string, unknown> | null {
    const widget = response.widgets.find((candidate) => candidate.type === 'bank_details');
    return widget?.data ?? null;
  }

  private recordValue(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null;
  }

  private cleanTransferValue(value: string): string {
    const trimmed = value.trim();
    return trimmed === '-' ? '' : trimmed;
  }

  private validateInstallmentTermsLocally(): QuickActionValidationFields {
    const fields: QuickActionValidationFields = {};
    const installment = this.installment();
    const current = this.response();
    const startOptions = current ? this.installmentStartOptions(current) : [];

    if (!isFutureOrTodayIsoDate(installment.start)
      || !startOptions.some((option) => option.id === installment.start)
      || !['01', '15'].includes(installment.start.slice(-2))) {
      fields['start'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidDate, this.i18n.t('p1.offeredStartDate'));
    }

      const duration = Number(installment.duration_months);
      const offered = this.response() ? this.installmentDurationOptions(this.response()!) : [];
	  const maxDuration = Number(this.response()?.form_schema?.conditions?.['maxDurationMonths'] ?? 6);
	  if (!Number.isInteger(duration) || !offered.some((option) => option.id === installment.duration_months)) {
	    fields['duration_months'] = this.localFieldError(
	      INPUT_VALIDATION_CODES.invalidModalPayload,
	      maxDuration === 2 ? 'Bitte wählen Sie 2 Raten.' : `Bitte wählen Sie eine Laufzeit von 2 bis ${maxDuration} Monaten.`,
	    );
      }

      if (!['transfer', 'sepa'].includes(installment.payment_method)) {
      fields['payment_method'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidModalPayload, this.i18n.t('quickActionWizard.validation.default.fields.payment_method'));
    }

    return fields;
  }

  private validateSepaLocally(): QuickActionValidationFields {
    const fields: QuickActionValidationFields = {};
    const sepa = this.sepa();
    const installmentContract = this.recordValue(this.response()?.metadata?.['installment_contract']);

    const ibanCode = ibanValidationCode(sepa.iban);
    if (ibanCode !== null) {
      fields['iban'] = this.localFieldError(ibanCode, this.i18n.t('sepaModal.ibanError'));
    }
    if (installmentContract && !/^[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/.test((sepa.bic ?? '').trim().toUpperCase())) {
      fields['bic'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidContactField, this.i18n.t('p1.validBic'));
    }
    if (this.sepaAccountHolder().trim().length < 2) {
      fields['kontoinhaber'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidContactField, this.i18n.t('quickActionWizard.validation.default.fields.kontoinhaber'));
    }
      const debitDay = installmentContract
        ? this.firstText(installmentContract, ['rate_day'])
        : sepa.abbuchungstag;
      if (installmentContract ? !isValidContractDebitDay(debitDay) : !isValidDebitDay(debitDay)) {
        fields['abbuchungstag'] = this.localFieldError(
          INPUT_VALIDATION_CODES.invalidDebitDay,
          installmentContract ? this.i18n.t('p1.installmentDebitDay') : this.i18n.t('p1.debitDay')
        );
      }
    if (!this.sepaMandateAccepted()) {
      fields['mandate'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidModalPayload, this.i18n.t('quickActionWizard.validation.default.fields.mandate'));
    }

    return fields;
  }

  private validatePaymentContractLocally(): QuickActionValidationFields {
    const fields: QuickActionValidationFields = {};
    if (!isValidContractDebitDay(this.paymentContract().abbuchungstag)) {
      fields['abbuchungstag'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidDebitDay, this.i18n.t('p1.installmentDebitDay'));
    }

    return fields;
  }

  private validateContactLocally(): QuickActionValidationFields {
    const fields: QuickActionValidationFields = {};
    const contact = this.contact();
    const email = contact.email.trim();
    const phone = contact.telefon.trim();
    const callbackTimeSlot = contact.callback_time_slot.trim();

    if (this.isInstallmentContactContext(this.response())) {
      if (email === '') {
        fields['email'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidEmail, this.i18n.t('p1.emailRequired'));
      } else if (!isValidEmail(email)) {
        fields['email'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidEmail, this.i18n.t('modalCommon.emailError'));
      } else if (phone !== '' && !isPlausiblePhone(phone)) {
        fields['telefon'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidContactField, this.i18n.t('modalCommon.phoneError'));
      }

      return fields;
    }

    if (email === '' && phone === '') {
      fields['contact'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidContactField, this.i18n.t('p1.contactRequired'));
    } else if (email !== '' && !isValidEmail(email)) {
      fields['email'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidEmail, this.i18n.t('modalCommon.emailError'));
    }
    if (callbackTimeSlot !== '' && phone === '') {
      fields['telefon'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidContactField, this.i18n.t('p1.callbackPhone'));
    } else if (phone !== '' && !isPlausiblePhone(phone)) {
      fields['telefon'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidContactField, this.i18n.t('modalCommon.phoneError'));
    }
    if (contact.message.trim() === '') {
      fields['message'] = this.localFieldError(INPUT_VALIDATION_CODES.invalidContactField, this.i18n.t('quickActionWizard.validation.default.fields.message'));
    }

    return fields;
  }

  private applyLocalValidation(step: string, fields: QuickActionValidationFields): boolean {
    if (Object.keys(fields).length === 0) {
      return true;
    }

    this.response.update((current) => current
      ? {
        ...current,
        status: 'validation_error',
        validation_errors: {
          valid: false,
          code: INPUT_VALIDATION_CODES.invalidModalPayload,
          message: this.localValidationSummary(step),
          fields,
        },
      }
      : current
    );
    this.error.set(null);
    this.devTestDataConfirmation.set(null);

    return false;
  }

  private localValidationSummary(step: string): string {
    const key = `quickActionWizard.validation.${step}.summary`;
    return this.i18n.t(key);
  }

  private localFieldError(code: string, message: string): { code: string; message: string } {
    return { code, message };
  }
}
